import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import type {
  CreateReimbursementTransactionInput,
  ReimbursementTransaction,
  ReimbursementTransactionStatus,
  UpdateReimbursementTransactionInput,
} from 'API';
import {
  createReimbursementTransaction as createReimbursementApi,
  getReimbursementTransactionsByPaidolId,
  updateReimbursementTransaction as updateReimbursementApi,
} from 'app/services/reimbursementService';
import type { RootState } from 'app/store/rootReducer';
import { isNotNullOrUndefined } from 'util/typeGuards';

const entityAdapter = createEntityAdapter<ReimbursementTransaction>({
  selectId: (reimbursement) => reimbursement.id,
});

const initialState = entityAdapter.getInitialState({
  loading: false,
  total: 0,
  currentFilter: null as ReimbursementTransactionStatus | null,
});

export type ReimbursementsState = typeof initialState;

export interface GetReimbursementsArgs {
  paidolId: string;
  status: ReimbursementTransactionStatus;
  paidolUserId?: string;
}

export const getReimbursements = createAsyncThunk(
  'reimbursements/getReimbursements',
  async ({ paidolId, status, paidolUserId }: GetReimbursementsArgs) => {
    const response = await getReimbursementTransactionsByPaidolId(paidolId, status, paidolUserId);
    return {
      items: response?.items?.filter(isNotNullOrUndefined) ?? [],
      status,
    };
  }
);

export const createReimbursement = createAsyncThunk(
  'reimbursements/createReimbursement',
  async (input: CreateReimbursementTransactionInput) => {
    const result = await createReimbursementApi(input);
    return result as ReimbursementTransaction;
  }
);

export const updateReimbursement = createAsyncThunk(
  'reimbursements/updateReimbursement',
  async (input: UpdateReimbursementTransactionInput) => {
    const result = await updateReimbursementApi(input);
    return result as ReimbursementTransaction;
  }
);

const reimbursementsSlice = createSlice({
  name: 'reimbursements',
  initialState,
  reducers: {
    applyLocalReimbursementUpdates: (
      state,
      action: PayloadAction<{ id: string; changes: Partial<ReimbursementTransaction> }[]>
    ) => {
      entityAdapter.updateMany(state, action.payload);
    },
    clearReimbursements: (state) => {
      entityAdapter.removeAll(state);
      state.total = 0;
      state.currentFilter = null;
    },
    removeReimbursement: (state, action: PayloadAction<string>) => {
      entityAdapter.removeOne(state, action.payload);
      state.total = Math.max(0, state.total - 1);
    },
    resetReimbursementsSlice: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getReimbursements.pending, (state) => {
        state.loading = true;
      })
      .addCase(getReimbursements.fulfilled, (state, action) => {
        entityAdapter.removeAll(state);
        entityAdapter.addMany(state, action.payload.items);
        state.total = action.payload.items.length;
        state.currentFilter = action.payload.status;
        state.loading = false;
      })
      .addCase(getReimbursements.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createReimbursement.fulfilled, (state, action) => {
        if (action.payload && state.currentFilter === action.payload.status) {
          entityAdapter.addOne(state, action.payload);
          state.total += 1;
        }
      })
      .addCase(updateReimbursement.fulfilled, (state, action) => {
        if (action.payload && state.currentFilter === action.payload.status) {
          entityAdapter.upsertOne(state, action.payload);
        }
      });
  },
});

export const {
  applyLocalReimbursementUpdates,
  clearReimbursements,
  removeReimbursement,
  resetReimbursementsSlice,
} = reimbursementsSlice.actions;

export const selectReimbursementsSlice = (state: RootState): ReimbursementsState =>
  state?.cards?.reimbursements ?? initialState;

const { selectAll: selectAllReimbursementsRaw, selectById: selectReimbursementByIdRaw } =
  entityAdapter.getSelectors(selectReimbursementsSlice);

export { selectReimbursementByIdRaw };

export const selectAllReimbursements = createSelector(
  selectAllReimbursementsRaw,
  (items): ReimbursementTransaction[] =>
    [...items].sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA;
    })
);

export const selectReimbursementById = (state: RootState, id: string) =>
  selectReimbursementsSlice(state).entities[id];

export const selectReimbursementsLoading = (state: RootState) => selectReimbursementsSlice(state).loading;

export default reimbursementsSlice.reducer;
